import React, { useState } from 'react';
import { X } from 'lucide-react';
import styles from './AddContributionModal.module.css';
import { useIntl } from 'react-intl';
import { getLocalDateString } from '../../../shared/lib/date/date-utils';
const getCurrencySymbol = currency => {
	const symbols = {
		USD: '$',
		EUR: '€',
		AMD: '֏',
		RUB: '₽',
	};
	return symbols[currency] || currency;
};

const getCurrencyFlag = currency => {
	const flags = {
		USD: '🇺🇸',
		EUR: '🇪🇺',
		AMD: '🇦🇲',
		RUB: '🇷🇺',
	};
	return flags[currency] || '';
};

const AddContributionModal = ({
	isOpen,
	onClose,
	goalName,
	onSubmit,
	goalCurrency = 'USD',
}) => {
	const intl = useIntl();
	const [amount, setAmount] = useState('');
	const [description, setDescription] = useState('');
	const [date, setDate] = useState(getLocalDateString());
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);

	if (!isOpen) return null;

	const validateForm = () => {
		const newErrors = {};

		const amountNum = parseFloat(amount);
		if (!amount || isNaN(amountNum)) {
			newErrors.amount = intl.formatMessage({
				id: 'AddGoalModalAddContributionModalErrorID1',
			});
		} else if (amountNum <= 0) {
			newErrors.amount = intl.formatMessage({
				id: 'AddGoalModalAddContributionModalErrorID2',
			});
		}

		if (!date) {
			newErrors.date = intl.formatMessage({
				id: 'AddGoalModalAddContributionModalErrorID3',
			});
		}

		if (description && description.length > 255) {
			newErrors.description = intl.formatMessage({
				id: 'AddGoalModalAddContributionModalErrorID4',
			});
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);

		try {
			const contributionData = {
				amount: parseFloat(amount),
				date: date,
				description: description.trim(),
			};

			if (onSubmit) {
				await onSubmit(contributionData);
			}

			// Reset form
			setAmount('');
			setDescription('');
			setDate(getLocalDateString());
		} catch (error) {
			console.error('Error in handleSubmit:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={styles.overlay} onClick={onClose}>
			<div className={styles.modal} onClick={e => e.stopPropagation()}>
				<button className={styles.closeButton} onClick={onClose}>
					<X size={20} />
				</button>

				<div className={styles.header}>
					<h3 className={styles.title}>
						{intl.formatMessage({ id: 'AddGoalModalAddContributionModalID1' })}
					</h3>
					<p className={styles.goalName}>
						{' '}
						{intl.formatMessage({ id: 'AddGoalModalAddContributionModalID2' })}
						{goalName}
					</p>
				</div>

				<div className={styles.formGroup}>
					<label className={styles.label}>
						{intl.formatMessage({ id: 'AddGoalModalAddContributionModalID3' })}
						{errors.date && (
							<span className={styles.errorText}> * {errors.date}</span>
						)}
					</label>
					<input
						type='date'
						className={styles.dateInput}
						value={date}
						onChange={e => setDate(e.target.value)}
						max={getLocalDateString()}
					/>
				</div>

				<div className={styles.formGroup}>
					<label className={styles.label}>
						Amount
						{errors.amount && (
							<span className={styles.errorText}> * {errors.amount}</span>
						)}
					</label>
					<div className={styles.amountRow}>
						<input
							type='number'
							className={styles.amountInput}
							value={amount}
							onChange={e => setAmount(e.target.value)}
							placeholder={intl.formatMessage({
								id: 'AddGoalModalAddContributionModalID4',
							})}
							step='0.01'
							min='0.01'
						/>
						<div className={styles.currencyDisplay}>
							<span className={styles.flag}>
								{getCurrencyFlag(goalCurrency)}
							</span>
							<span className={styles.currencyCode}>{goalCurrency}</span>
							<span className={styles.currencySymbol}>
								({getCurrencySymbol(goalCurrency)})
							</span>
						</div>
					</div>
				</div>

				<div className={styles.formGroup}>
					<label className={styles.label}>
						{intl.formatMessage({ id: 'AddGoalModalAddContributionModalID5' })}
						{errors.description && (
							<span className={styles.errorText}> * {errors.description}</span>
						)}
					</label>
					<textarea
						className={styles.textarea}
						value={description}
						onChange={e => setDescription(e.target.value)}
						rows={3}
						placeholder={intl.formatMessage({
							id: 'AddGoalModalAddContributionModalID6',
						})}
						maxLength={255}
					/>
					<div className={styles.charCount}>
						{description.length}/{' '}
						{intl.formatMessage({ id: 'AddGoalModalAddContributionModalID7' })}
					</div>
				</div>

				<button
					className={styles.submitButton}
					onClick={handleSubmit}
					disabled={loading}
				>
					{loading
						? intl.formatMessage({ id: 'AddGoalModalAddContributionModalID8' })
						: intl.formatMessage({ id: 'AddGoalModalAddContributionModalID9' })}
				</button>
			</div>
		</div>
	);
};

export default AddContributionModal;
